import { TopPage } from "@/components/elements/TopPage";
import { getDynamicRoute } from "@/utils/url";
import { Stack, styled } from "@mui/material";
import { FC, useMemo, useState, useEffect } from "react";
import { matchRoutes, Outlet, useLocation, useParams } from "react-router-dom";
import { AppRoutes } from "@/routes/config";
import { getCompanyMenu, TCompanyMenu } from "@/features/company/constants/companyMenu";
import { GenericTabs } from "@/components/commons/GenericTabs";
import { blue } from "@/theme/colors";
import { InnerBox } from "@/components/elements/InnerBox";
import { useCompanyContractStatusCount } from "@/api/services/admin/company";

type TCompanyLayoutProps = {};

export const CompanyLayout: FC<TCompanyLayoutProps> = () => {
  const { id } = useParams();
  const { pathname } = useLocation();

  const { data: contractStatusCount } = useCompanyContractStatusCount();

  const companyMenu = useMemo(() => getCompanyMenu(contractStatusCount?.data), [contractStatusCount]);

  const activeIndex = companyMenu.findIndex(
    ({ url, extraUrls }: TCompanyMenu) =>
      matchRoutes([{ path: url }], pathname) !== null || extraUrls?.some((extraUrl: string) => matchRoutes([{ path: extraUrl }], pathname) !== null),
  );
  const [value, setValue] = useState(activeIndex !== -1 ? activeIndex : 0);

  useEffect(() => {
    if (activeIndex !== -1 && activeIndex !== value) {
      setValue(activeIndex);
    }
  }, [activeIndex, pathname]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const isExtraUrlActive = useMemo(() => {
    return companyMenu.some(({ extraUrls }: TCompanyMenu) =>
      extraUrls?.some((extraUrl: string) => matchRoutes([{ path: extraUrl }], pathname) !== null),
    );
  }, [pathname, companyMenu]);

  const backUrl = useMemo(() => {
    if (isExtraUrlActive) {
      const activeMenu = companyMenu.find(({ extraUrls }: TCompanyMenu) =>
        extraUrls?.some((extraUrl: string) => matchRoutes([{ path: extraUrl }], pathname) !== null),
      );
      return activeMenu ? getDynamicRoute(activeMenu.url, { id }) : AppRoutes.companyDuringContractList;
    }
    return AppRoutes.companyDuringContractList;
  }, [isExtraUrlActive, pathname, id, companyMenu]);

  return (
    <Stack gap={2.5} sx={{ mt: 1 }}>
      {isExtraUrlActive ? (
        <TopPage
          title="受け入れ企業の一覧"
          description="システムに登録されている受け入れ企業の一覧です。新規登録する場合は「新規顧客登録」で追加した後に顧客ユーザーを登録してください。"
          backUrl={backUrl}
        />
      ) : (
        <TopPage
          title="受け入れ企業の一覧"
          description="システムに登録されている受け入れ企業の一覧です。新規登録する場合は「新規顧客登録」で追加した後に顧客ユーザーを登録してください。"
          enablePageBack={false}
        />
      )}
      <GenericTabs
        colorBorder={blue[80]}
        tabs={companyMenu.map(({ title, url }: TCompanyMenu) => ({ label: title, url }))}
        value={value}
        onChange={handleChange}
        id={id}
      />
      <OutletBox>
        <Outlet />
      </OutletBox>
    </Stack>
  );
};

const OutletBox = styled(InnerBox)`
  margin-top: -20px;
  padding-top: 8px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
`;
